<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import StudentCommentRepository from '@/repositories/StudentCommentRepository';
import type { StudentCommentRequest } from '@/types/Request/StudentCommentRequest';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type FlightSchool from '@/types/FlightSchool';
import type { StudentCommentResponse } from '@/types/Response/StudentCommentResponse';
import { useUserStore } from '@/stores/user';

const props = defineProps<{
    student: FlightSchoolCourseStudent,
    course: FlightSchoolCourse,
    school: FlightSchool,
}>();

const $q = useQuasar();
const timeToggle = ref<string|null>(null);
const comment = ref('');
const hours = ref<null|number>(null);
const minutes = ref<null|number>(null);
const isLoading = ref<boolean>(false);
const ruleNotEmpty = (val:string) => ((val && val.length > 0 && val.trim().length > 0) || 'This field is required');
const emit = defineEmits(['update:comments']);
const userStore = useUserStore();

const addComment = async (): Promise<StudentCommentResponse> => {
    const multiplier = timeToggle.value === 'add' ? 1 : -1;
    const studentComment = {
        comment: comment.value,
        adminCustomerId: userStore.user?.id,
        timeSpent: (Number(hours.value) * 60 + Number(minutes.value)) * 60 * multiplier,
    } as StudentCommentRequest;

    return StudentCommentRepository.addStudentComment(props.school, props.course, props.student, studentComment);
};

const resetForm = () => {
    timeToggle.value = null;
    comment.value = '';
    hours.value = null;
    minutes.value = null;
};

const submit = async () => {
    const totalTime = (Number(hours.value) * 60 + Number(minutes.value)) * 60;

    if (timeToggle.value === null && totalTime !== 0) {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            message: 'Please clear hours and/or minutes if you are not making a time adjustment.',
            icon: 'warning',
        });

        return;
    }

    if (timeToggle.value !== null && totalTime === 0) {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            message: 'Please enter a nonzero value for hours or minutes if you are making a time adjustment.',
            icon: 'warning',
        });

        return;
    }

    isLoading.value = true;
    const newComment = await addComment().catch(() => {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
            message: 'Something went wrong',
        });

        isLoading.value = false;
    });

    if (newComment) {
        $q.notify({
            color: 'green-4',
            textColor: 'white',
            icon: 'cloud_done',
            message: 'Comment submitted successfully.',
        });

        emit('update:comments');
        resetForm();
    }
    isLoading.value = false;
};
</script>
<template>
    <q-form
        id="comment-form"
        class="q-gutter-md bg-white q-pa-md q-mx-auto q-my-auto container comment-form"
        @submit="submit"
    >
        <div class="header text-weight-bold">
            Add New Comment
        </div>
        <div class="text-weight-bold faux-radio-btns q-ml-sm">
            <q-checkbox
                v-model="timeToggle"
                class="time-toggle-btn add"
                value="add"
                true-value="add"
                :false-value="null"
                label="Add Time"
                indeterminate-icon="radio_button_unchecked"
                checked-icon="radio_button_checked"
                unchecked-icon="radio_button_unchecked"
            />
            <q-checkbox
                v-model="timeToggle"
                class="time-toggle-btn remove"
                value="remove"
                true-value="remove"
                :false-value="null"
                label="Remove Time"
                indeterminate-icon="radio_button_unchecked"
                checked-icon="radio_button_checked"
                unchecked-icon="radio_button_unchecked"
            />
        </div>
        <div class="row text-inputs">
            <div class="col-md-auto col-12 hours q-pb-sm">
                <q-input
                    v-model="hours"
                    class="hours-input q-pr-xs"
                    type="number"
                    inputmode="numeric"
                    :min="0"
                    :max="99"
                    square
                    outlined
                />
                <div class="input-label q-pr-lg">
                    Hour(s)
                </div>
            </div>
            <div class="col-md-auto col-12 minutes q-pb-sm">
                <q-input
                    v-model="minutes"
                    class="minutes-input q-pr-xs"
                    type="number"
                    inputmode="numeric"
                    :min="0"
                    :max="59"
                    stack-label
                    square
                    outlined
                />
                <div class="input-label">
                    Minute(s)
                </div>
            </div>
            <div class="col-12 comments q-pt-md">
                <div class="input-label text-weight-bold">
                    Comments (required)
                </div>
                <q-input
                    v-model="comment"
                    class="comment-input q-mt-sm q-mr-md"
                    type="textarea"
                    square
                    outlined
                    no-error-icon
                    :rules="[ruleNotEmpty]"
                    lazy-rules="ondemand"
                    required
                    rows="10"
                />
            </div>
        </div>
        <q-btn
            id="submit-btn"
            label="Submit"
            type="submit"
            class="submit-btn fsum-button-dark-blue q-mt-lg q-mb-md"
            no-caps
            :loading="isLoading"
        />
    </q-form>
</template>
<style scoped lang="scss">
.header {
    font-size: 32px;
}

.time-toggle-btn {
    font-size: 14px;
    color: var(--fsum-black);
    display: flex;
}

.text-inputs {
    .hours, .minutes {
        .hours-input, .minutes-input {
            display: inline-block;
        }
        .input-label {
            display: inline;
            font-size: 14px;
            color: var(--fsum-black);
            overflow: visible;
        }
        :deep(.q-field__control) {
            width: 48px;
            height: 48px;
        }
    }

    .comments {
        .input-label {
            font-size: 16px;
            color: var(--fsum-black);
        }
        :deep(.q-textarea textarea) {
            padding-top: 5px;
            resize: none;
        }
    }

    :deep(input::-webkit-outer-spin-button),
    :deep(input::-webkit-inner-spin-button) {
        -webkit-appearance: none;
        margin: 0;
    }
    :deep(.q-field__bottom--animated) {
        padding-left: 0px;
    }
}
</style>
